import React, { lazy, useState, Suspense, useEffect } from "react";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Header from "../../components/header";
import Sidebar from "../../components/sidebar";
import { Alert, AlertTitle, Box, CircularProgress } from "@mui/material";
import { protectedRoutes } from "../index";
import { Member, PageType, RouteInterface } from "../../types";
import { useSetRecoilState } from "recoil";
import {
  useAuthorize,
  useGetUserMembership,
} from "../../mutationsQueries/user";
import { socketAtom } from "../../selectorsAtoms";
import GlobalSharedStateClass from "../../util/globalSharedStateClass";
import SnackbarWrapper from "../../components/snackbarWrapper";
import { LOGIN_PAGE, VOUCHERS_EDIT_PAGE } from "../../util/constants";
import { useStyles } from "./styles";

const routeComponents: Record<
  string,
  React.LazyExoticComponent<React.ComponentType>
> = {
  vouchersEditPage: lazy(() => import("pages/vouchersEditPage")),
  "vouchersPage/vouchersListPage": lazy(
    () => import("pages/vouchersPage/vouchersListPage")
  ),
  "organizationPages/organizationsListPage": lazy(
    () => import("pages/organizationPages/organizationsListPage")
  ),
  "organizationPages/organizationPage": lazy(
    () => import("pages/organizationPages/organizationPage")
  ),
};

// Database-upgrade maintenance banner. Shows on every protected page except
// the voucher edit page (which strips the wrapper's top padding and manages
// its own layout), from deploy until this local-time (CET) cutoff — after
// which it hides itself, no redeploy needed. Remove the banner block and this
// constant once the upgrade is done.
const MAINTENANCE_BANNER_UNTIL = new Date("2026-10-14T00:00:00");

const getRouteComponent = (srcPath?: string, component?: string) => {
  if (!component) return undefined;
  const key = srcPath ? `${srcPath}/${component}` : component;
  return routeComponents[key];
};
const ProtectedPages = () => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const setSocket = useSetRecoilState<WebSocket | undefined>(socketAtom);
  const location = useLocation();
  const classes = useStyles();
  const searchParams = new URLSearchParams(document.location.search);
  const token: string | null = searchParams.get("token");
  const { data, isLoading } = useAuthorize(
    token || localStorage.getItem("token")
  );
  // Memberships carry the user's email/name (the authorization payload doesn't);
  // fetched once authorized so we can attach full identity to Sentry.
  const { data: memberships } = useGetUserMembership(!!data);
  const cache = new GlobalSharedStateClass();

  const connectSocket = () => {
    if (token) return;
    const wsDomain = import.meta.env.VITE_MY_PAPERFLOW_API_URL
      ? import.meta.env.VITE_MY_PAPERFLOW_API_URL.split("//")[1]
      : "";
    const ws: WebSocket = new WebSocket(
      `wss://${wsDomain}/v1/websocket?access_token=${localStorage.getItem(
        "token"
      )}`
    );
    setSocket(ws);

    ws.onclose = function (e) {
      console.log(
        "Socket is closed. Reconnect will be attempted in 1 second.",
        e.reason
      );
      setTimeout(() => {
        connectSocket();
      }, 1000);
    };

    ws.onerror = function (err) {
      ws.close();
    };
  };

  useEffect(() => {
    if (!token) {
      setIsAuthorized(true);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthorized) {
      connectSocket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  // Identify the logged-in user for Sentry. Set the id as soon as authorized,
  // then enrich with email/name once memberships load.
  useEffect(() => {
    if (!data) return;
    // Prefer the membership for the active org (correct level/org name); fall back
    // to any membership of this user just for identity (email/name are the same
    // across all of a user's memberships).
    const orgMemberships = memberships as Member[] | undefined;
    const member =
      orgMemberships?.find(m => m.organization_id === data.organization_id) ??
      orgMemberships?.find(m => m.user_id === data.user_id);

    Sentry.setUser({
      id: String(data.user_id),
      email: member?.user_email,
      username: member
        ? `${member.user_first_name} ${member.user_last_name}`.trim()
        : undefined,
    });

    // Tags are indexed, so issues can be filtered/searched by these.
    Sentry.setTag("user_id", String(data.user_id));
    Sentry.setTag("user_type", data.user_type);
    Sentry.setTag("organization_id", String(data.organization_id));
    Sentry.setTag("auth_mode", token ? "embed" : "session");
    if (member?.level) Sentry.setTag("membership_level", member.level);

    // Richer, non-indexed detail shown on the issue.
    Sentry.setContext("account", {
      organization_id: data.organization_id,
      organization_name: member?.organization_name,
      user_type: data.user_type,
      authorization_type: data.authorization_type,
      membership_level: member?.level,
      scopes: data.scopes,
    });
  }, [data, memberships, token]);

  useEffect(() => {
    const TWO_HOURS = 7200;
    const SEVEN_DAYS = 7 * 24 * 60 * 60;

    if (data) {
      cache.setAuthData(data);

      if (token) {
        if (data?.expires_in && data.expires_in > TWO_HOURS) {
          setIsAuthorized(false);
        } else {
          setIsAuthorized(true);
        }
      } else {
        // If not deep login, check is token older than 7 days, if true, redirect to login page
        if (data?.expires_in && data.expires_in > SEVEN_DAYS) {
          setIsAuthorized(false);
        } else {
          setIsAuthorized(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, token]);

  if (isLoading || isAuthorized === null) {
    return (
      <CircularProgress className={classes.progress} size={40} thickness={4} />
    );
  } else if (isAuthorized === false) {
    return <Navigate to={LOGIN_PAGE} />;
  }

  return (
    <>
      <Header />

      <Box
        display={"flex"}
        className={clsx(classes.wrapper, {
          [classes.hidePadding]: location.pathname.includes(VOUCHERS_EDIT_PAGE),
        })}
      >
        <Sidebar />
        <Box className={classes.content}>
          {new Date() < MAINTENANCE_BANNER_UNTIL &&
            !location.pathname.includes(VOUCHERS_EDIT_PAGE) && (
              <Alert severity="info" variant="filled" sx={{ mb: 2 }}>
                We are doing routine system maintenance next Tuesday, October
                13th 2026. Paperflow will be unavailable between 16:00 and 19:00
                — your vouchers and data are not affected.
              </Alert>
            )}
          <Suspense
            fallback={
              <CircularProgress
                className={classes.progress}
                size={40}
                thickness={4}
              />
            }
          >
            <Routes>
              {protectedRoutes.map((page: RouteInterface) => {
                const Component = getRouteComponent(
                  page.srcPath,
                  page.component
                );
                return (
                  <Route
                    key={page.path}
                    path={page.path}
                    element={Component && <Component />}
                  >
                    {page?.pages?.map((item: PageType) => {
                      const Component = getRouteComponent(
                        item.srcPath,
                        item.component
                      );
                      return (
                        <Route
                          key={item.component}
                          path={item.path}
                          index={item.index}
                          element={Component && <Component />}
                        />
                      );
                    })}
                  </Route>
                );
              })}
            </Routes>
          </Suspense>
        </Box>
      </Box>
      <SnackbarWrapper />
    </>
  );
};

export default ProtectedPages;
